import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { api, formatApiError } from "../context/AuthContext";
import { BedDouble, Bath, Users, ArrowUpRight, Sparkles, ClipboardList, HandPlatter, LineChart } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import RevenueCalculator from "../components/RevenueCalculator";
import { resolveUrl } from "../components/PhotoDropzone";
import StatsStrip from "../components/StatsStrip";
import TestimonialsSection from "../components/TestimonialsSection";
import FAQSection from "../components/FAQSection";
import ConciergeChat from "../components/ConciergeChat";

const HomePage = () => {
  const { t, lang } = useLang();
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/houses/public")
      .then(({ data }) => setHouses(data))
      .catch(() => setHouses([]))
      .finally(() => setLoading(false));
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const serviceIcons = [Sparkles, HandPlatter, ClipboardList, LineChart];

  return (
    <div className="bg-[#F4F1EB] min-h-screen text-[#0A1128]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 rise">
            <div className="overline mb-6">{t.hero.overline}</div>
            <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[1.05] tracking-tight">
              {t.hero.title_1}<br />
              {t.hero.title_2}<br />
              <span className="italic text-[#C5A059]">{t.hero.title_3}</span>
            </h1>
            <p className="mt-8 max-w-xl text-[#3A435E] text-base md:text-lg leading-relaxed font-light">
              {t.hero.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#contact"
                className="bg-[#0A1128] text-white hover:bg-[#162243] transition-colors duration-300 px-8 py-4 text-xs tracking-[0.22em] uppercase"
                data-testid="hero-cta-contact"
              >
                {t.hero.cta_primary}
              </a>
              <a
                href="#houses"
                className="bg-transparent border border-[#0A1128] hover:bg-[#0A1128] hover:text-white transition-colors duration-300 px-8 py-4 text-xs tracking-[0.22em] uppercase"
                data-testid="hero-cta-houses"
              >
                {t.hero.cta_secondary}
              </a>
            </div>
          </div>
          <div className="md:col-span-5 rise rise-delay-1">
            <div className="relative overflow-hidden border border-[#0A1128]/10 aspect-[4/5]">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_property-dashboard-43/artifacts/qu9vt9er_casa_lisboa_terraco.webp"
                alt="Terraço em Lisboa StayZen"
                className="w-full h-full object-cover grayscale-[0.08] hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
              />
              <div className="absolute bottom-6 left-6 right-6 text-[#FCFAF8] text-xs tracking-[0.22em] uppercase flex justify-between">
                <span>— Lisboa</span>
                <span>Portugal</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STATS */}
      <StatsStrip />

      {/* ABOUT */}
      <section className="border-t border-[#0A1128]/10" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-6">{t.about.overline}</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight">
              {t.about.title}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-[#3A435E] text-lg leading-relaxed font-light">
              {t.about.body}
            </p>
            <ul className="mt-10 space-y-5">
              {t.about.bullets.map((b, i) => (
                <li key={`bullet-${i}-${b.slice(0, 20)}`} className="flex items-start gap-4 border-t border-[#0A1128]/10 pt-5">
                  <span className="font-serif-display italic text-[#C5A059] text-lg shrink-0 w-6">0{i + 1}</span>
                  <span className="text-[#0A1128] text-base">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-[#FCFAF8] border-y border-[#0A1128]/10" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="max-w-2xl">
            <div className="overline mb-6">{t.services.overline}</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight">
              {t.services.title}
            </h2>
          </div>
          <div className="mt-16 grid md:grid-cols-2 gap-x-12 gap-y-14">
            {t.services.list.map((s, i) => {
              const Icon = serviceIcons[i % serviceIcons.length];
              return (
                <div
                  key={`svc-${s.title || i}`}
                  className="group border-t border-[#0A1128]/15 pt-6 flex gap-6"
                  data-testid={`service-card-${i}`}
                >
                  <Icon size={28} strokeWidth={1.3} className="shrink-0 text-[#C5A059] mt-1" />
                  <div>
                    <div className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128]">
                      {s.title}
                    </div>
                    <p className="mt-3 text-[#3A435E] leading-relaxed font-light">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <RevenueCalculator />

      {/* HOUSES */}
      <section id="houses" data-testid="houses-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <div className="overline mb-6">{t.houses.overline}</div>
              <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight">
                {t.houses.title}
              </h2>
            </div>
            <div className="text-sm text-[#3A435E] font-light">
              {houses.length} {houses.length === 1 ? "" : ""}
            </div>
          </div>
          {loading ? (
            <div className="mt-16 text-[#3A435E]" data-testid="houses-loading">…</div>
          ) : (
            <div className="mt-16 grid md:grid-cols-12 gap-8 md:gap-10">
              {houses.map((h, i) => (
                <article
                  key={h.id}
                  className={`group ${
                    i % 4 === 0 ? "md:col-span-7" : i % 4 === 1 ? "md:col-span-5" : i % 4 === 2 ? "md:col-span-5" : "md:col-span-7"
                  }`}
                  data-testid={`house-card-${i}`}
                >
                  <div className="relative overflow-hidden border border-[#0A1128]/10 aspect-[4/3]">
                    <img
                      src={resolveUrl(h.cover_image)}
                      alt={h.name}
                      className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <div className="overline text-[#C5A059]">{h.location}</div>
                      <h3 className="font-serif-display text-2xl md:text-3xl font-light mt-2 text-[#0A1128]">
                        {h.name}
                      </h3>
                      <div className="mt-3 flex items-center gap-5 text-sm text-[#3A435E]">
                        <span className="flex items-center gap-1.5"><Users size={14} strokeWidth={1.4} /> {h.max_guests}</span>
                        <span className="flex items-center gap-1.5"><BedDouble size={14} strokeWidth={1.4} /> {h.bedrooms}</span>
                        <span className="flex items-center gap-1.5"><Bath size={14} strokeWidth={1.4} /> {h.bathrooms}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      {h.nightly_price > 0 && (
                        <>
                          <div className="font-serif-display text-2xl md:text-3xl font-light text-[#C5A059]" data-testid={`house-price-${h.id}`}>
                            €{Math.round(h.nightly_price)}
                          </div>
                          <div className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E]/70 mt-1">
                            {t.houses.per_night}
                          </div>
                        </>
                      )}
                      <ArrowUpRight size={22} strokeWidth={1.2} className="text-[#0A1128]/40 group-hover:text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500 ml-auto mt-3" />
                    </div>
                  </div>
                  <p className="mt-4 text-[#3A435E] font-light leading-relaxed">{h.description}</p>
                  <div className="mt-5">
                    <Link
                      to={lang === "pt" ? `/reservar/${h.id}` : `/book/${h.id}`}
                      className="inline-flex items-center gap-2 bg-[#C5A059] text-[#0A1128] hover:bg-[#DCC287] transition-colors duration-300 px-6 py-3 text-xs tracking-[0.22em] uppercase"
                      data-testid={`book-house-${h.id}`}
                    >
                      {t.houses.book}
                      <ArrowUpRight size={14} strokeWidth={1.5} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* FAQ */}
      <FAQSection />

      {/* CONTACT */}
      <section id="contact" className="bg-[#0A1128] text-[#F4F1EB]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-[#C5A059]">{t.contact.overline}</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light mt-6 leading-tight">
              {t.contact.title}
            </h2>
            <p className="mt-8 text-[#F4F1EB]/70 font-light">stayzenpremium@gmail.com</p>
            <p className="text-[#F4F1EB]/70 font-light">+351 935 316 194</p>
          </div>
          <form onSubmit={submitContact} className="md:col-span-6 md:col-start-7 space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.name}
                className="bg-transparent border-b border-[#F4F1EB]/30 focus:border-[#C5A059] outline-none py-3 text-[#F4F1EB] placeholder:text-[#F4F1EB]/40 text-sm tracking-wide"
                data-testid="contact-field-name"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.contact.email}
                className="bg-transparent border-b border-[#F4F1EB]/30 focus:border-[#C5A059] outline-none py-3 text-[#F4F1EB] placeholder:text-[#F4F1EB]/40 text-sm tracking-wide"
                data-testid="contact-field-email"
              />
            </div>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder={t.contact.phone}
              className="w-full bg-transparent border-b border-[#F4F1EB]/30 focus:border-[#C5A059] outline-none py-3 text-[#F4F1EB] placeholder:text-[#F4F1EB]/40 text-sm tracking-wide"
              data-testid="contact-field-phone"
            />
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t.contact.message}
              className="w-full bg-transparent border-b border-[#F4F1EB]/30 focus:border-[#C5A059] outline-none py-3 text-[#F4F1EB] placeholder:text-[#F4F1EB]/40 text-sm tracking-wide resize-none"
              data-testid="contact-field-message"
            />
            <button
              type="submit"
              disabled={sending}
              className="bg-[#C5A059] text-[#0A1128] hover:bg-[#DCC287] transition-colors duration-300 px-8 py-4 text-xs tracking-[0.22em] uppercase disabled:opacity-60"
              data-testid="contact-submit"
            >
              {sending ? "…" : t.contact.submit}
            </button>
          </form>
        </div>
      </section>

      <Footer />
      <ConciergeChat />
    </div>
  );
};

export default HomePage;
